import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Note } from '@/api/entities';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useUser } from '@/contexts/UserContext';
import { useTranslation } from '@/components/utils/translations';

function normalizeId(value) {
  if (value == null) return null;
  const s = String(value).trim();
  return s || null;
}

export default function Notes({ accountId, personaId, personId, onNotesCountChange }) {
  const { t } = useTranslation();
  const contextUser = useUser();
  const currentUserId = normalizeId(contextUser?.user_id) || normalizeId(contextUser?.id);

  const effectiveAccountId = useMemo(() => normalizeId(accountId), [accountId]);
  const effectivePersonaId = useMemo(() => normalizeId(personaId), [personaId]);
  const effectivePersonId = useMemo(() => normalizeId(personId), [personId]);
  /**
   * Note.filter expects the Person row id (PersonFinal.id), not the stable person_id string.
   * Prefer explicit `personId` from parents that know the DB key (e.g. PersonProfile).
   */
  const effectivePersonScopedId = useMemo(
    () => effectivePersonId || effectivePersonaId,
    [effectivePersonId, effectivePersonaId]
  );
  const hasScope = Boolean(effectiveAccountId || effectivePersonScopedId);

  const [notes, setNotes] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  /** Any person/persona id we should match on note rows (API often only allows filter by account_id). */
  const personNoteMatchIds = useMemo(() => {
    const ids = [effectivePersonId, effectivePersonaId].filter(Boolean).map(String);
    return [...new Set(ids)];
  }, [effectivePersonId, effectivePersonaId]);

  const loadNotes = useCallback(async () => {
    if (!hasScope) {
      setNotes([]);
      setLoadError(null);
      setIsLoading(false);
      if (onNotesCountChange && typeof onNotesCountChange === 'function') {
        onNotesCountChange(0);
      }
      return;
    }

    setIsLoading(true);
    setLoadError(null);
    try {
      let fetchedNotes;
      if (effectiveAccountId && personNoteMatchIds.length > 0) {
        // Entity Note GET often only allows account_id in where; narrow to this person client-side
        const raw = await Note.filter({ account_id: effectiveAccountId }, '-created_at');
        const list = Array.isArray(raw) ? raw : [];
        const match = new Set(personNoteMatchIds);
        fetchedNotes = list.filter((n) => {
          const pid = n.person_id != null ? String(n.person_id) : '';
          const paid = n.persona_id != null ? String(n.persona_id) : '';
          return (pid && match.has(pid)) || (paid && match.has(paid));
        });
      } else if (effectiveAccountId) {
        fetchedNotes = await Note.filter({ account_id: effectiveAccountId }, '-created_at');
      } else if (effectivePersonScopedId) {
        fetchedNotes = await Note.filter({ person_id: effectivePersonScopedId }, '-created_at');
      } else {
        fetchedNotes = [];
      }

      fetchedNotes = Array.isArray(fetchedNotes) ? fetchedNotes : [];
      setNotes(fetchedNotes);

      if (onNotesCountChange && typeof onNotesCountChange === 'function') {
        onNotesCountChange(fetchedNotes.length);
      }
    } catch (error) {
      const status = error?.status;
      console.error('Error loading notes:', error);
      if (status === 401) {
        window.location.href = '/Auth';
        return;
      }
      if (status === 400) {
        setLoadError('acctNotesLoadErrorContext');
      } else {
        setLoadError('acctNotesLoadErrorGeneric');
      }
      setNotes([]);
      if (onNotesCountChange && typeof onNotesCountChange === 'function') {
        onNotesCountChange(0);
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    hasScope,
    effectiveAccountId,
    effectivePersonScopedId,
    personNoteMatchIds,
    onNotesCountChange,
  ]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const handleAddNote = async () => {
    if (!newNote.trim() || !hasScope) return;

    try {
      const noteData = {
        content: newNote.trim(),
        ...(effectiveAccountId && { account_id: effectiveAccountId }),
        ...(!effectiveAccountId && effectivePersonScopedId && { person_id: effectivePersonScopedId }),
        ...(effectiveAccountId &&
          effectivePersonScopedId && { person_id: effectivePersonId || effectivePersonaId || effectivePersonScopedId }),
      };

      await Note.create(noteData);
      setNewNote('');
      setIsAdding(false);
      await loadNotes();
    } catch (error) {
      const status = error?.status;
      console.error('Error creating note:', error);
      if (status === 401) {
        window.location.href = '/Auth';
        return;
      }
      toast.error(status === 400 ? t('acctCouldNotSaveThisNote') : t('acctFailedToSaveNote'));
    }
  };

  const handleDeleteNote = async (noteId, noteUserId) => {
    const ownerId = normalizeId(noteUserId);
    if (ownerId && currentUserId && ownerId !== currentUserId) {
      toast.error(t('acctCannotDeleteNote'));
      return;
    }
    try {
      await Note.delete(noteId);
      await loadNotes();
    } catch (error) {
      const status = error?.status;
      console.error('Error deleting note:', error);
      if (status === 401) {
        window.location.href = '/Auth';
        return;
      }
      if (status === 403) {
        toast.error(t('acctCannotDeleteNote'));
        return;
      }
      toast.error(t('acctFailedToDeleteNote'));
    }
  };

  const canDeleteNote = (note) => {
    const ownerId = normalizeId(note?.user_id);
    if (!ownerId) return true;
    if (!currentUserId) return true;
    return ownerId === currentUserId;
  };

  const authorLabel = (note) => {
    const ownerId = normalizeId(note?.user_id);
    if (ownerId && currentUserId && ownerId === currentUserId) return t('acctNoteAuthorYou');
    if (note?.created_by && String(note.created_by).trim()) return note.created_by;
    if (!ownerId) return t('acctNoteAuthorTeam');
    return t('acctNoteAuthorTeammate');
  };

  if (!hasScope) {
    return null;
  }

  if (isLoading) {
    return <div className="text-center py-4 text-[#5C6B7A]">{t('acctLoadingNotes')}</div>;
  }

  if (loadError) {
    return (
      <div className="text-center py-6 text-sm text-red-600 border border-red-100 rounded-lg bg-red-50 px-4">
        {t(loadError)}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-[#0A1A2F]">{t('notes')}</h3>
        <Button
          onClick={() => setIsAdding(true)}
          size="sm"
          className="bg-[#00C2C2] hover:bg-[#00A3A3]"
          disabled={isAdding}
        >
          <Plus className="w-4 h-4 mr-1" />
          {t('acctAddNote')}
        </Button>
      </div>

      {isAdding && (
        <Card className="border-[#00C2C2]/20 bg-[#00C2C2]/5">
          <CardContent className="p-4">
            <Textarea
              placeholder={t('acctWriteNotePlaceholder')}
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              className="mb-3"
              rows={3}
            />
            <div className="flex gap-2">
              <Button onClick={handleAddNote} size="sm" className="bg-[#00C2C2] hover:bg-[#00A3A3]">
                {t('acctSaveNote')}
              </Button>
              <Button
                onClick={() => {
                  setIsAdding(false);
                  setNewNote('');
                }}
                variant="outline"
                size="sm"
              >
                {t('cancel')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {notes.length === 0 ? (
        <div className="text-center py-8 text-[#5C6B7A]">
          <p>{t('acctNoNotesYet')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <Card key={note.id} className="border-0 shadow-md">
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <Badge variant="outline" className="text-xs">
                    {authorLabel(note)}
                  </Badge>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#5C6B7A]">
                      {format(new Date(note.created_at), 'MMM dd, yyyy HH:mm')}
                    </span>
                    {canDeleteNote(note) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteNote(note.id, note.user_id)}
                        className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                        aria-label={t('acctDeleteNote')}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-[#0A1A2F] whitespace-pre-wrap">{note.content}</p>
                {note.tags && note.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {note.tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
